import { Component, inject, OnInit, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { DamageReport } from '../../../models/damage-report';
import { FormControl } from '@angular/forms';
import { DamageReportsService } from '../../../services/damage-reports/damage-reports-service';
import { NavBadgeService } from '../../../services/nav-badge/nav-badge-service';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { Params } from '../../../services/utilis/add-params-service';
import { AcknowledgeDamageReportForm } from '../../../@shared/forms/damage-reports/acknowledge-damage-report-form/acknowledge-damage-report-form';
import { RejectDamageReportForm } from '../../../@shared/forms/damage-reports/reject-damage-report-form/reject-damage-report-form';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { DamageType, ReportType } from '../../../models/enums';
import { Card } from 'primeng/card';
import { DamageReportDetails } from '../damage-report-details/damage-report-details';
import { GenericPaginated } from '../../../models/generic-paginated';

@Component({
  selector: 'app-damage-reports-list',
  imports: [TableComponent, ButtonModule, AcknowledgeDamageReportForm, RejectDamageReportForm, Card, DamageReportDetails],
  templateUrl: './damage-reports-list.html',
  styleUrl: './damage-reports-list.scss'
})
export class DamageReportsList implements OnInit {

  params = [
    'page', 'size', 'search', 'sortBy', 'sortDirection',
    'createdFrom', 'createdTo', 'reportType', 'damageType', 'status',
  ]

  modifiedParams: Params = {
    page: 'page', size: 'size', search: 'search',
    sortBy: 'sortBy', sortDirection: 'sortDirection',
    createdFrom: 'createdFrom', createdTo: 'createdTo',
    reportType: 'reportType', damageType: 'damageType', status: 'status',
  }

  tableColumns: DynamicColumn<DamageReport>[] = [
    { header: 'ID', field: 'id', sortable: true },
    {
      header: 'Name', field: 'customerFirstName', mergedName: 'customerLastName',
      bottomField: 'customerEmail', isAvatar: true, sortable: false,
    },
    {
      header: 'Plate No', field: 'plateNo', sortable: false,
      command: (r) => this.redirectToVehiclesPage(r),
      cellClass: 'text-primary underline cursor-pointer',
    },
    { header: 'Report Type', field: 'reportType', sortable: true, isCapitalize: true },
    {
      header: 'Damage Type', field: 'damageTypes', sortable: false, isCapitalize: true,
      valueGetter: (r: DamageReport) => (r.damageTypes ?? []).map(d => d.charAt(0) + d.slice(1).toLowerCase()).join(', '),
    },
    {
      header: 'Status', field: 'status', sortable: true,
      isStatusBadge: true,
      badgeStyleGetter: (value) => {
        const map: Record<string, { background: string; color: string }> = {
          'ACKNOWLEDGED': { background: 'var(--status-available-bg, #dcfce7)', color: 'var(--status-available-text, #16a34a)' },
          'REJECTED':     { background: 'var(--status-rejected-bg, #fee2e2)', color: 'var(--status-rejected-text, #dc2626)' },
          'PENDING':      { background: 'var(--status-pending-bg, #fef9c3)', color: 'var(--status-pending-text, #854d0e)' },
        };
        return map[value as string] ?? { background: 'transparent', color: 'inherit' };
      },
    },
    { header: 'Description', field: 'description', sortable: false },
    { header: 'Rejection Reason', field: 'rejectionReason', sortable: false },
    { header: 'Reviewed At', field: 'reviewedAt', sortable: true, isDateTime: true },
    { header: 'Created At', field: 'createdAt', sortable: true, isDateTime: true },
    {
      field: 'actions', header: 'Photo', sortable: false,
      actions: [
        {
          icon: 'camera-icon.svg',
          buttonClass: 'camera-action-btn',
          command: (r: DamageReport) => this.openLightbox(r),
          tooltip: 'View report photos',
          badgeGetter: (r: DamageReport) => r.images?.length ?? 0,
        },
      ],
    },
  ];

  reports = signal<DamageReport[]>([]);
  totalRecords = signal(0);
  pendingCount = signal(0);
  acknowledgedCount = signal(0);
  rejectedCount = signal(0);
  first = 0;
  rows = 20;
  activeTab = 'PENDING';

  selectedReport: DamageReport;
  selectedReportIndex = 0;
  detailsDialog = false;

  lightboxOpen = false;
  lightboxReport: DamageReport | null = null;
  lightboxIndex = 0;

  searchControl = new FormControl('');
  acknowledgeReportDialog = false;
  rejectReportDialog = false;

  private reportsCache = new Map<string, GenericPaginated<DamageReport>>();
  private lastNonTabParams = '';

  reportTypeFilter: CustomFilter = {
    key: 'reportType', label: 'Report Type', type: 'select',
    options: enumToOptions(ReportType),
  }

  damageTypeFilter: CustomFilter = {
    key: 'damageType', label: 'Damage Type', type: 'multiselect',
    options: enumToOptions(DamageType),
  }

  createdDateFilter: CustomFilter = {
    key: 'created', label: 'Date', type: 'date-range', options: [],
  }

  private damageReportsService = inject(DamageReportsService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private navBadgeService = inject(NavBadgeService)

  ngOnInit() {
    this.loadCounts();

    const currentParams = this.activatedRoute.snapshot.queryParams;
    if (!currentParams['status']) {
      this.router.navigate([], {
        queryParams: { status: 'PENDING', page: 0 },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.getDamageReportsList();

    this.activatedRoute.queryParams.subscribe(params => {
      this.activeTab = params['status'] ?? 'PENDING';
    });
  }

  setTab(status: string) {
    this.activeTab = status;
    if (status !== 'PENDING') this.detailsDialog = false;
    this.router.navigate([], {
      queryParams: { status, page: 0 },
      queryParamsHandling: 'merge',
    });
  }

  loadCounts() {
    this.damageReportsService.getDamageStats()
      .subscribe(res => {
        this.pendingCount.set(res.data.pending);
        this.acknowledgedCount.set(res.data.acknowledged);
        this.rejectedCount.set(res.data.rejected);
      });
  }

  getDamageReportsList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const tab = queryParams['status'] ?? 'PENDING';
        const nonTabParams = { ...queryParams };
        delete nonTabParams['status'];
        const nonTabKey = JSON.stringify(nonTabParams);

        if (nonTabKey !== this.lastNonTabParams) {
          this.reportsCache.clear();
          this.lastNonTabParams = nonTabKey;
        }

        if (this.reportsCache.has(tab)) {
          return of(this.reportsCache.get(tab) as GenericPaginated<DamageReport>);
        }

        const params: Record<string, string | number> = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
          status: tab,
        };
        return this.damageReportsService.getDamageReportsList(params).pipe(
          tap(res => this.reportsCache.set(tab, res))
        );
      })
    ).subscribe((res) => {
      this.reports.set(res.data.content);
      this.totalRecords.set(res.data.totalElements);
      this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize;

      if (this.activeTab === 'PENDING' && res.data.content.length > 0) {
        this.openDetails(res.data.content[0]);
      }
    });
  }

  openDetails(report: DamageReport) {
    this.selectedReport = report;
    this.selectedReportIndex = this.reports().findIndex(r => r.id === report.id);
    this.detailsDialog = true;
  }

  openDetailsFromShow(report: DamageReport) {
    this.selectedReport = report;
    this.selectedReportIndex = this.reports().findIndex(r => r.id === report.id);
    this.detailsDialog = true;
  }

  onDetailsPrev = () => {
    const idx = this.selectedReportIndex - 1;
    if (idx >= 0) { this.selectedReport = this.reports()[idx]; this.openDetails(this.selectedReport); }
  }

  onDetailsNext = () => {
    const idx = this.selectedReportIndex + 1;
    if (idx < this.reports().length) { this.selectedReport = this.reports()[idx]; this.openDetails(this.selectedReport); }
  }

  openAcknowledgeReport = () => {
    this.acknowledgeReportDialog = true;
  }

  onAcknowledgeReportCanceled = () => {
    this.acknowledgeReportDialog = false;
  }

  onReportAcknowledged = () => {
    this.reportsCache.clear();
    this.loadCounts();
    this.navBadgeService.refresh();
    this.acknowledgeReportDialog = false;
    this.detailsDialog = false;
    this.getDamageReportsList();
  }

  openRejectReport = () => {
    this.rejectReportDialog = true;
  }

  onRejectReportCanceled = () => {
    this.rejectReportDialog = false;
  }

  onReportRejected = () => {
    this.reportsCache.clear();
    this.loadCounts();
    this.navBadgeService.refresh();
    this.rejectReportDialog = false;
    this.detailsDialog = false;
    this.getDamageReportsList();
  }

  openLightbox = (report: DamageReport) => {
    if (!report.images?.length) return;
    this.lightboxReport = report;
    this.lightboxIndex = 0;
    this.lightboxOpen = true;
  }

  lightboxPrev = () => { if (this.lightboxIndex > 0) this.lightboxIndex--; }
  lightboxNext = () => { if (this.lightboxReport && this.lightboxIndex < this.lightboxReport.images.length - 1) this.lightboxIndex++; }

  onPageChange = (event: PaginatorState) => {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge',
    });
  }

  redirectToVehiclesPage = (report: DamageReport) => {
    this.router.navigate(['/vehicles'], { queryParams: { search: report.plateNo }, queryParamsHandling: '' });
  }

  redirectToCustomersPage = (report: DamageReport) => {
    this.router.navigate(['/customers'], { queryParams: { search: report.customerEmail }, queryParamsHandling: '' });
  }
}
